import {Box, HStack, Show, Text, ThemeColorTextPath} from 'platform/foundation';
import {cva} from 'platform/shared';
import {match, Pattern} from 'ts-pattern';

import {KeyboardEvent, MouseEvent, forwardRef} from 'react';

import {always, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';

export interface ChipProps extends FormControlProps<boolean>, TestIdProps {
  label: string;
  suffix?: string;
  size?: 'small' | 'default';
  variant?: 'default' | 'ghost';
  isFocused?: boolean;
  isError?: boolean;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  tabIndex?: number;
  ariaLabel?: string;
}

const chipVariants = cva(
  'inline-block cursor-pointer rounded-[var(--radius-lg)] transition-[background-color] duration-[250ms] ease-[ease]',
  {
    variants: {
      isSelected: {
        true: '',
        false: '',
      },
      variant: {
        default: '',
        ghost: '',
      },
      isDisabled: {
        true: 'pointer-events-none opacity-40',
        false: '[pointer-events:auto] opacity-100',
      },
      isFocused: {
        true: '[outline:var(--outline-default)_var(--color-accent)] [outline-offset:var(--outline-offset)]',
        false: '',
      },
      isError: {
        true: 'border border-solid border-[var(--color-severity-danger)]',
        false: '',
      },
    },
    compoundVariants: [
      {
        isSelected: true,
        variant: 'ghost',
        className:
          'bg-[var(--color-palettes-blue-20-100)] hover:bg-[var(--color-palettes-blue-20-100)]',
      },
      {
        isSelected: false,
        variant: 'ghost',
        className: 'bg-transparent hover:bg-[var(--color-palettes-neutral-40-100)]',
      },
      {
        isSelected: true,
        variant: 'default',
        className: 'bg-[var(--color-accent)] hover:bg-[var(--color-accent)]',
      },
      {
        isSelected: false,
        variant: 'default',
        className:
          'bg-[var(--color-palettes-neutral-30-100)] hover:bg-[var(--color-palettes-neutral-40-100)]',
      },
    ],
    defaultVariants: {
      variant: 'default',
      isDisabled: false,
      isFocused: false,
      isError: false,
    },
  }
);

export const Chip = forwardRef<HTMLDivElement, ChipProps>((props, ref) => {
  const handleOnClick = (event: MouseEvent<HTMLElement>) => {
    if (props.isDisabled) {
      event.preventDefault();
      return;
    }

    props.onChange?.(props.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (props.isDisabled) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      props.onChange?.(props.value);
    }

    props.onKeyDown?.(event);
  };

  const isSmall = props.size === 'small';
  const isSelected = !!props.value;

  const textColor = match<[boolean, ChipProps['variant']], ThemeColorTextPath>([
    isSelected,
    props.variant,
  ])
    .with([true, 'ghost'], always('link'))
    .with([false, 'ghost'], always('primary'))
    .with([true, Pattern.any], always('white'))
    .otherwise(always('primary'));

  return (
    <div
      ref={ref}
      data-testid={suffixTestId('chip', props)}
      onClick={handleOnClick}
      onKeyDown={handleKeyDown}
      tabIndex={props.tabIndex ?? -1}
      aria-checked={props.value ? true : undefined}
      aria-disabled={props.isDisabled}
      aria-label={props.ariaLabel ?? props.label}
      role="checkbox"
      className={chipVariants({
        isSelected,
        variant: props.variant,
        isDisabled: Boolean(props.isDisabled),
        isFocused: Boolean(props.isFocused),
        isError: Boolean(props.isError),
      })}
    >
      <Box paddingHorizontal={isSmall ? 2 : 3}>
        <HStack align="center" spacing={1} height={isSmall ? 6 : 8}>
          <Text
            color={textColor}
            size={isSmall ? 'xSmall' : 'small'}
            noWrap
            data-testid={suffixTestId('chipContent', props)}
          >
            {props.label}
          </Text>
          <Show when={isNotNil(props.suffix)}>
            <Text color={textColor} size="xSmall" noWrap>
              {props.suffix}
            </Text>
          </Show>
        </HStack>
      </Box>
    </div>
  );
});
